import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { Activity, ArrowRight, Check, Crown, Loader2, MessageSquareText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { getPublicStoreCatalog, getStoreUrgency, type StoreCatalogItem } from '../lib/db-storage';
import { syncSubscriptionAfterPayment } from '../lib/sync-subscription';
import { trackSubscription } from '../lib/analytics';
import { useSEO, COMPETITIVE_KEYWORD_CLUSTERS, mergeKeywordSets } from '../lib/seo';
import { PricingRTIDemo } from '../components/pricing/PricingRTIDemo';

function findSubscriptionRow(
  items: StoreCatalogItem[],
  tier: string,
  periodKey: 'monthly' | 'annual',
): StoreCatalogItem | undefined {
  const aliases =
    tier.toLowerCase() === 'premium'
      ? ['premium', 'control', 'basic']
      : tier.toLowerCase() === 'pro'
        ? ['pro', 'transformation']
        : [tier.toLowerCase()];
  const strict = items.find(
    (i) =>
      i.kind === 'subscription' &&
      i.active !== false &&
      i.show_on_pricing === true &&
      aliases.includes(String(i.plan_tier || '').toLowerCase()) &&
      String(i.billing_period_key || '').toLowerCase() === periodKey,
  );
  if (strict) return strict;
  return items.find(
    (i) =>
      i.kind === 'subscription' &&
      i.active !== false &&
      aliases.includes(String(i.plan_tier || '').toLowerCase()) &&
      String(i.billing_period_key || '').toLowerCase() === periodKey,
  );
}

function pricePerMonthDisplay(row: StoreCatalogItem | undefined, fallback: string): string {
  if (!row || row.amount_cents == null) return fallback;
  if (String(row.billing_interval || '').toLowerCase() === 'year') {
    return (row.amount_cents / 100 / 12).toFixed(2);
  }
  return (row.amount_cents / 100).toFixed(2);
}

function asStringFeatures(mf: unknown): string[] {
  if (!Array.isArray(mf)) return [];
  return mf.map((x) => String(x));
}

export default function Pricing() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [loading, setLoading] = useState(false);
  const [catalogItems, setCatalogItems] = useState<StoreCatalogItem[]>([]);
  const [catalogNotice, setCatalogNotice] = useState<string>('');
  const [urgency, setUrgency] = useState<{ foundingSold: number; foundingCap: number; foundingRemaining: number } | null>(null);
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentSuccessSyncStarted = useRef(false);
  const autoTrialCheckoutStarted = useRef(false);
  useSEO({
    title: 'Relationship App Pricing | TENSION',
    description:
      'Compare TENSION plans for relationship intelligence, conflict prevention, and communication coaching. Start with a trial and upgrade when ready.',
    keywords: mergeKeywordSets(
      COMPETITIVE_KEYWORD_CLUSTERS.core as unknown as string[],
      COMPETITIVE_KEYWORD_CLUSTERS.conflict as unknown as string[],
      ['relationship app pricing', 'couples app pricing', 'communication coach pricing', 'best relationship app'],
    ),
    canonical: '/pricing',
    ogType: 'product',
    twitterCard: 'summary_large_image',
  });
  // Defensive: handle potential undefined auth during hot reload
  const user = auth?.user || null;
  const accessToken = auth?.accessToken || null;

  // Stripe Checkout success_url: /pricing?payment=success — auto-sync like Settings → Sync (retries for webhook lag)
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment !== 'success') {
      paymentSuccessSyncStarted.current = false;
      return;
    }

    if (!accessToken) {
      const t = window.setTimeout(() => {
        if (paymentSuccessSyncStarted.current) return;
        paymentSuccessSyncStarted.current = true;
        setSearchParams({}, { replace: true });
        toast.success('Payment received! Sign in to activate your plan on this device.');
      }, 12000);
      return () => window.clearTimeout(t);
    }

    if (paymentSuccessSyncStarted.current) return;
    paymentSuccessSyncStarted.current = true;

    setSearchParams({}, { replace: true });

    void (async () => {
      const token = accessToken;
      const toastId = toast.loading('Activating your plan…');
      const result = await syncSubscriptionAfterPayment(token);
      toast.dismiss(toastId);

      if (result.ok === false) {
        toast.error(result.error || 'Could not activate your plan. Try Settings → Subscription → Sync.');
        return;
      }

      if (result.activated) {
        const p = result.subscription.plan;
        const label = p.charAt(0).toUpperCase() + p.slice(1);
        toast.success(`You're on ${label}. Welcome!`);
        if (p === 'premium' || p === 'pro') {
          trackSubscription(p);
        }
        return;
      }

      toast.success(
        'Payment received. If your plan still shows Awareness, open Settings → Subscription and tap Sync in a minute.',
        { duration: 8000 },
      );
    })();
  }, [searchParams, setSearchParams, accessToken]);

  useEffect(() => {
    const payment = searchParams.get('payment');
    if (payment === 'cancelled') {
      toast.info('Payment cancelled. You can try again anytime.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    getPublicStoreCatalog()
      .then((r) => {
        setCatalogItems(r.items || []);
        if (r.catalogError) {
          setCatalogNotice('Store catalog is partially unavailable. Showing cached pricing defaults.');
        } else {
          setCatalogNotice('');
        }
      })
      .catch(() => {
        setCatalogItems([]);
        setCatalogNotice('Store catalog is unavailable. Showing pricing defaults.');
      });
  }, []);

  useEffect(() => {
    getStoreUrgency().then(setUrgency);
  }, []);

  const periodKey = billingCycle === 'monthly' ? 'monthly' : 'annual';
  const premiumRow = findSubscriptionRow(catalogItems, 'premium', periodKey);
  const proRow = findSubscriptionRow(catalogItems, 'pro', periodKey);
  const foundingRow = catalogItems.find((i) => i.kind === 'founding' && i.active !== false);

  const premiumPrice = pricePerMonthDisplay(premiumRow, billingCycle === 'monthly' ? '9.99' : '7.99');
  const proPrice = pricePerMonthDisplay(proRow, billingCycle === 'monthly' ? '19.99' : '15.99');

  const premiumFeatures = asStringFeatures(premiumRow?.marketing_features);
  const proFeatures = asStringFeatures(proRow?.marketing_features);
  const controlLabel = (premiumRow?.name || '').trim() || 'Control';
  const controlSubtitle = (premiumRow?.subtitle || '').trim() || 'Take control before things spiral.';
  const transformationLabel = (proRow?.name || '').trim() || 'Transformation';
  const transformationSubtitle = (proRow?.subtitle || '').trim() || 'Completely change how you connect.';

  const foundingPassCap = foundingRow?.founding_spots_cap ?? 100;
  
  const foundingSold = urgency?.foundingSold ?? 0;
  const foundingRemaining = urgency?.foundingRemaining ?? Math.max(0, foundingPassCap - foundingSold);
  const demoRef = useRef<HTMLDivElement | null>(null);
  const pricingRef = useRef<HTMLElement | null>(null);
  const handleStartFree = () => {
    if (user) {
      navigate('/home');
      return;
    }
    navigate('/auth');
  };

  const handleSubscribe = async (plan: string, billingOverride?: 'monthly' | 'annual') => {
    const selectedBilling = billingOverride ?? billingCycle;
    if (!user) {
      toast.info('Create your account to start a 7-day trial. Card is required for paid tiers.');
      navigate(`/auth?mode=signup&startTrial=1&trialPlan=${encodeURIComponent(plan)}&trialBilling=${encodeURIComponent(selectedBilling)}`);
      return;
    }

    if (!accessToken) {
      toast.error('Session expired. Please sign in again.');
      navigate('/auth');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/create-subscription-checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`,
            'X-User-Token': accessToken,
          },
          body: JSON.stringify({
            plan,
            billingPeriod: selectedBilling,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          toast.error('Session expired. Please sign in again.');
          setTimeout(() => navigate('/auth'), 2000);
        } else {
          toast.error(data.error || 'Checkout failed. Please try again.');
        }
        
        setLoading(false);
        return;
      }

      if (data.url) {
        toast.success('Opening checkout in new tab...');
        
        // Open Stripe in a new tab (not current window)
        const stripeWindow = window.open(data.url, '_blank');
        
        if (!stripeWindow) {
          toast.error('Please allow popups to complete checkout');
        } else {
          toast.info('Complete checkout in the new tab. We activate your plan when you return here.', {
            duration: 5000,
          });
        }
        
        setLoading(false);
      } else {
        toast.error('No checkout URL received');
        setLoading(false);
      }
      
    } catch (error) {
      toast.error('Network error. Please check your connection.');
      setLoading(false);
    }
  };

  useEffect(() => {
    const startTrial = searchParams.get('startTrial') === '1';
    const trialPlan = searchParams.get('trialPlan');
    const trialBillingRaw = searchParams.get('trialBilling');
    const trialBilling = trialBillingRaw === 'annual' ? 'annual' : 'monthly';
    const validPlan = trialPlan === 'premium' || trialPlan === 'pro' ? trialPlan : null;
    if (!startTrial || !validPlan) {
      autoTrialCheckoutStarted.current = false;
      return;
    }
    if (!user || !accessToken || autoTrialCheckoutStarted.current) return;
    autoTrialCheckoutStarted.current = true;
    setBillingCycle(trialBilling);
    setSearchParams({}, { replace: true });
    void handleSubscribe(validPlan, trialBilling);
  }, [searchParams, setSearchParams, user, accessToken]);

  return (
    <div className="relative min-h-screen overflow-hidden tension-page-bg">
      <div className="pointer-events-none absolute -left-24 top-20 h-64 w-64 rounded-full bg-purple-500/20 blur-3xl" />
      <div className="pointer-events-none absolute right-0 top-48 h-72 w-72 rounded-full bg-pink-500/20 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-1/3 h-80 w-80 rounded-full bg-violet-500/10 blur-3xl" />
      <motion.div
        aria-hidden
        animate={{ y: [0, -10, 0], x: [0, 6, 0] }}
        transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut' }}
        className="pointer-events-none absolute left-12 top-40 h-28 w-28 rounded-full border border-white/10 bg-white/5 blur-2xl"
      />

      <main className="relative z-10 mx-auto max-w-6xl px-6 pb-10 pt-4 sm:pb-14 sm:pt-6">
        <motion.section
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: 'easeInOut' }}
          className="relative overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-br from-neutral-950 via-purple-950/55 to-pink-950/45 px-6 py-12 shadow-[0_24px_70px_rgba(15,23,42,0.5)] sm:px-10"
        >
          <div className="pointer-events-none absolute inset-0 animate-pulse bg-[radial-gradient(circle_at_top,rgba(236,72,153,0.22),transparent_55%),radial-gradient(circle_at_bottom_right,rgba(147,51,234,0.26),transparent_50%)]" />
          <div className="pointer-events-none absolute -left-16 top-10 h-44 w-44 rounded-full bg-pink-400/20 blur-3xl" />
          <div className="pointer-events-none absolute -right-20 bottom-8 h-56 w-56 rounded-full bg-violet-500/20 blur-3xl" />
          <div className="relative z-10 mx-auto max-w-3xl text-center text-white">
            <p className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs tracking-wide text-white/90">
              <Sparkles className="h-3.5 w-3.5" />
              Relationship intelligence in real time
            </p>
            <h2 className="text-2xl font-light leading-tight sm:text-4xl lg:text-5xl">
              Most relationships don&apos;t fail from lack of care. They fail from lack of clarity in the moment.
            </h2>
            <p className="mx-auto mt-5 max-w-2xl text-sm text-white/80 sm:text-base">
              TENSION gives you real-time insight into what&apos;s actually happening before things escalate.
            </p>
            <p className="mx-auto mt-3 max-w-2xl text-xs text-white/70 sm:text-sm">
              Built for people who want to understand, repair, and respond better.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button
                onClick={handleStartFree}
                className="w-full rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-2.5 text-white transition-all duration-300 ease-in-out hover:from-purple-700 hover:to-pink-700 sm:w-auto"
              >
                Try it free
              </Button>
              <Button
                variant="outline"
                onClick={() => demoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="w-full rounded-2xl border-white/40 bg-white/10 px-6 py-2.5 text-white transition-all duration-300 ease-in-out hover:bg-white/20 sm:w-auto"
              >
                See how it works in 30 seconds
              </Button>
            </div>
          </div>
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0, rotate: -2 }}
            transition={{ duration: 0.45, ease: 'easeInOut', delay: 0.15 }}
            className="absolute -bottom-6 right-6 hidden w-64 rounded-2xl border border-white/25 bg-white/10 p-4 shadow-[0_20px_50px_rgba(20,14,40,0.45)] backdrop-blur-xl xl:block"
          >
            <p className="text-xs uppercase tracking-wide text-white/70">Live RTI preview</p>
            <div className="mt-3 space-y-3">
              <div className="rounded-xl border border-white/20 bg-black/20 p-3">
                <div className="flex items-center justify-between text-xs text-white/75">
                  <span className="inline-flex items-center gap-1"><Activity className="h-3.5 w-3.5" /> Score</span>
                  <span className="font-medium text-pink-200">72 Rising</span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-white/15">
                  <motion.div
                    initial={{ width: '15%' }}
                    animate={{ width: ['44%', '72%', '58%', '72%'] }}
                    transition={{ duration: 3.6, repeat: Infinity, ease: 'easeInOut' }}
                    className="h-1.5 rounded-full bg-gradient-to-r from-purple-400 to-pink-400"
                  />
                </div>
              </div>
              <div className="rounded-xl border border-white/20 bg-black/20 p-3 text-xs text-white/75">
                <p className="inline-flex items-center gap-1"><MessageSquareText className="h-3.5 w-3.5" /> Message analysis</p>
                <p className="mt-1 text-white/90">Guarded frustration detected. Suggested de-escalation response ready.</p>
              </div>
            </div>
          </motion.div>
          <motion.div
            aria-hidden
            initial={false}
            animate={{ opacity: 1, y: [0, -4, 0] }}
            transition={{ duration: 4.6, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute right-12 top-8 hidden rounded-full border border-pink-200/30 bg-pink-400/15 px-3 py-1 text-[11px] text-pink-100 lg:block"
          >
            Real-time clarity
          </motion.div>
        </motion.section>

        <div ref={demoRef}>
          <PricingRTIDemo onStartFree={handleStartFree} />
        </div>

        <motion.section
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="mt-12"
        >
          <h3 className="text-center text-2xl font-light text-neutral-100 sm:text-3xl">
            Stop reacting to tension. Start understanding it.
          </h3>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="rounded-3xl border border-white/15 bg-neutral-950/65 p-6 shadow-[0_16px_40px_rgba(15,23,42,0.35)] backdrop-blur-lg"
            >
              <h4 className="text-lg font-medium text-white">Before TENSION</h4>
              <ul className="mt-4 space-y-3 text-sm text-white/80">
                {['Miscommunication', 'Escalation', 'Guessing emotions', 'Reactive responses'].map((line) => (
                  <li key={line} className="flex items-center gap-3">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full border border-white/20 bg-white/10">
                      <span className="h-2 w-2 rounded-full bg-white/50" />
                    </span>
                    {line}
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={false}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className="rounded-3xl border border-purple-300/40 bg-gradient-to-br from-neutral-900/80 via-purple-950/45 to-pink-950/45 p-6 shadow-[0_20px_50px_rgba(147,51,234,0.3)] backdrop-blur-lg"
            >
              <h4 className="text-lg font-medium text-white">With TENSION</h4>
              <ul className="mt-4 space-y-3 text-sm text-white/90">
                {['Real-time clarity', 'De-escalation', 'Intent understanding', 'Confident responses'].map((line) => (
                  <li key={line} className="flex items-center gap-3">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full border border-purple-300/45 bg-purple-400/20">
                      <Check className="h-4 w-4 text-pink-200" />
                    </span>
                    {line}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </motion.section>

        <section ref={pricingRef} className="relative mt-12">
          <div className="pointer-events-none absolute left-1/2 top-28 h-72 w-72 -translate-x-1/2 rounded-full bg-purple-500/15 blur-3xl" />
          <div className="mb-6 text-center">
            <h3 className="text-2xl font-light text-neutral-100 sm:text-3xl">Choose the clarity level you need right now</h3>
            {catalogNotice ? (
              <p className="mt-2 text-xs text-amber-200">{catalogNotice}</p>
            ) : null}
          </div>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 ease-in-out ${
                billingCycle === 'monthly'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'border border-white/20 bg-white/10 text-white/80 hover:bg-white/15'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`relative rounded-xl px-4 py-2 text-sm font-medium transition-all duration-200 ease-in-out ${
                billingCycle === 'annual'
                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                  : 'border border-white/20 bg-white/10 text-white/80 hover:bg-white/15'
              }`}
            >
              Annual
              <span className="absolute -right-2 -top-2 rounded-full bg-green-500 px-2 py-0.5 text-xs text-white">
                Save 20%
              </span>
            </button>
          </div>

          <div className="relative mt-8 grid gap-5 lg:grid-cols-3">
            <motion.div
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="rounded-3xl border border-white/15 bg-neutral-950/65 p-6 shadow-[0_18px_40px_rgba(15,23,42,0.35)] backdrop-blur-lg"
            >
              <p className="text-sm font-medium uppercase tracking-wide text-white/65">Awareness</p>
              <p className="mt-1 text-3xl font-light text-white">$0</p>
              <p className="mt-1 text-sm text-white/75">Start seeing what&apos;s really happening.</p>
              <ul className="mt-5 space-y-2 text-sm text-white/80">
                {[
                  'Basic interaction insights',
                  'Emotional tone detection',
                  'Relationship clarity preview',
                  'Start recognizing patterns',
                ].map((line) => (
                  <li key={line} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-green-300" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant="outline"
                onClick={handleStartFree}
                className="mt-6 w-full rounded-2xl border-white/25 bg-white/10 text-white transition-all duration-300 ease-in-out hover:bg-white/20 hover:shadow-md"
              >
                Start at Awareness
              </Button>
              <p className="mt-2 text-xs text-white/60">No credit card required.</p>
            </motion.div>

            <motion.div
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="relative rounded-3xl border-2 border-pink-300/55 bg-gradient-to-br from-neutral-900 via-purple-950/65 to-pink-950/60 p-6 text-white shadow-[0_26px_72px_rgba(147,51,234,0.42)] backdrop-blur-xl"
            >
              <div className="pointer-events-none absolute inset-0 rounded-3xl bg-[radial-gradient(circle_at_top,rgba(236,72,153,0.16),transparent_52%)]" />
              <div className="pointer-events-none absolute -bottom-16 left-1/2 h-28 w-56 -translate-x-1/2 rounded-full bg-pink-500/25 blur-2xl" />
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 px-3 py-1 text-xs font-medium text-white">
                Most couples choose this
              </span>
              <p className="text-sm font-medium uppercase tracking-wide text-white/80">{controlLabel}</p>
              <p className="mt-1 text-3xl font-light">${premiumPrice}</p>
              <p className="mt-1 text-sm text-white/70">per month{billingCycle === 'annual' ? ', billed annually' : ''}</p>
              <p className="mt-2 text-sm text-white/75">{controlSubtitle}</p>
              <ul className="mt-5 space-y-2 text-sm text-white/90">
                {(premiumFeatures.length ? premiumFeatures.slice(0, 6) : [
                  'Real-time RTI insights',
                  'Guided response suggestions',
                  'Conflict de-escalation support',
                  'Emotional pattern recognition',
                  'Live interaction risk alerts',
                  'Priority support response time',
                ]).map((line) => (
                  <li key={line} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-pink-200" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-end gap-1.5">
                {[24, 16, 30, 20, 34, 26, 18, 28].map((h, i) => (
                  <motion.span
                    key={`${h}-${i}`}
                    className="w-1.5 rounded-full bg-gradient-to-t from-purple-400/70 to-pink-300/90"
                    animate={{ height: [h - 8, h, h - 4, h] }}
                    transition={{ duration: 2.4, repeat: Infinity, delay: i * 0.08, ease: 'easeInOut' }}
                  />
                ))}
              </div>
              <Button
                onClick={() => handleSubscribe('premium')}
                disabled={loading}
                className="mt-6 w-full rounded-2xl bg-white text-neutral-900 transition-all duration-300 ease-in-out hover:shadow-[0_14px_35px_rgba(255,255,255,0.2)]"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start free trial'}
              </Button>
              <p className="mt-2 text-xs text-white/70">7-day trial, then automatic billing to card on file.</p>
            </motion.div>

            <motion.div
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="rounded-3xl border border-white/15 bg-neutral-950/65 p-6 shadow-[0_18px_40px_rgba(15,23,42,0.35)] backdrop-blur-lg"
            >
              <p className="text-sm font-medium uppercase tracking-wide text-white/65">{transformationLabel}</p>
              <p className="mt-1 text-3xl font-light text-white">${proPrice}</p>
              <p className="mt-1 text-sm text-white/70">per month{billingCycle === 'annual' ? ', billed annually' : ''}</p>
              <p className="mt-2 text-sm text-white/75">{transformationSubtitle}</p>
              <ul className="mt-5 space-y-2 text-sm text-white/80">
                {(proFeatures.length ? proFeatures.slice(0, 6) : [
                  'Advanced RTI breakdowns',
                  'Long-term pattern tracking',
                  'Deeper emotional intelligence insights',
                  'Relationship growth guidance',
                  'Pro-level coaching workflows',
                  'Priority feature access and beta drops',
                ]).map((line) => (
                  <li key={line} className="flex gap-2">
                    <Check className="mt-0.5 h-4 w-4 text-purple-300" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => handleSubscribe('pro')}
                disabled={loading}
                className="mt-6 w-full rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white transition-all duration-300 ease-in-out hover:shadow-[0_12px_30px_rgba(168,85,247,0.4)]"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Start 7-day trial'}
              </Button>
              <p className="mt-2 text-xs text-white/70">7-day trial, then automatic billing to card on file.</p>
            </motion.div>
          </div>
        </section>

        <motion.section
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="mt-12 rounded-3xl border border-white/15 bg-neutral-950/70 p-8 text-center shadow-[0_18px_45px_rgba(15,23,42,0.4)] backdrop-blur-lg"
        >
          <p className="text-xl font-light text-white">
            Paid tiers include a 7-day trial. We collect a card up front and only bill after trial ends.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-4 text-sm text-white/75">
            {['Cancel anytime', 'No commitment', 'No pressure', 'Built to help, not trap you'].map((item) => (
              <span key={item} className="rounded-full border border-white/20 bg-white/10 px-3 py-1">
                {item}
              </span>
            ))}
          </div>
        </motion.section>

        <section className="mt-8 rounded-3xl border border-pink-300/35 bg-gradient-to-r from-purple-900/55 to-pink-900/45 px-6 py-4 text-center shadow-[0_14px_34px_rgba(147,51,234,0.25)] backdrop-blur-lg">
          <p className="animate-pulse text-sm font-medium text-white">
            Early access pricing - limited time.
          </p>
          <p className="mt-1 text-xs text-white/75">
            Founding users are locked in at this rate.
          </p>
          <p className="mt-1 text-xs text-white/65">
            {urgency ? `${foundingRemaining}/${foundingPassCap} founding spots remaining` : 'Loading founding spot count...'}
          </p>
        </section>

        <motion.section
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="mt-12"
        >
          <h3 className="text-center text-2xl font-light text-neutral-100">Social Proof</h3>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {[
              {
                quote: '"I didn\'t realize how much I was misreading things until I saw it in real time."',
                label: 'Early User',
              },
              {
                quote: '"This changes how you respond before the conversation gets worse."',
                label: 'Beta Tester',
              },
              {
                quote: '"It feels like emotional clarity when you need it most."',
                label: 'Founding Member',
              },
            ].map((item, idx) => (
              <motion.div
                key={item.quote}
                initial={false}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.08, duration: 0.3, ease: 'easeInOut' }}
                className="rounded-3xl border border-white/15 bg-neutral-950/70 p-5 shadow-[0_16px_40px_rgba(15,23,42,0.35)] backdrop-blur-lg"
              >
                <p className="text-sm leading-relaxed text-white/80">{item.quote}</p>
                <p className="mt-3 text-xs font-medium uppercase tracking-wide text-white/60">{item.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={false}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="mt-14 rounded-3xl border border-white/15 bg-neutral-950/70 p-10 text-center shadow-[0_20px_60px_rgba(15,23,42,0.4)] backdrop-blur-lg"
        >
          <h3 className="text-3xl font-light text-white sm:text-4xl">Stop guessing. Start understanding.</h3>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-white/75">
            Use TENSION before the moment becomes a misunderstanding.
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              onClick={() => pricingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 px-8 py-2.5 text-white transition-all duration-300 ease-in-out hover:shadow-lg"
            >
              Start free now
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => demoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              className="rounded-2xl border-white/25 bg-white/10 px-6 py-2.5 text-white hover:bg-white/20"
            >
              Review live demo
            </Button>
          </div>
          <div className="mt-6 text-xs text-white/60">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1">
              <Crown className="h-3.5 w-3.5 text-purple-300" />
              Early users shape the product roadmap directly
            </span>
          </div>
        </motion.section>
      </main>
    </div>
  );
}